import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';

/**
 * Cliente de Web Push del frontend (PR #9).
 *
 *  - Registra el service worker `sw-push.js`.
 *  - Pide permission al user (`Notification.requestPermission()`).
 *  - Llama a `PushManager.subscribe({applicationServerKey})` con la VAPID
 *    public key que devuelve el backend (`GET /api/push/vapid-public-key`).
 *  - Manda la subscription al backend (`POST /api/push/subscribe`).
 *  - Permite desuscribirse (`DELETE /api/push/unsubscribe`).
 *
 *  La regla del browser: si el user ya dió permission antes (incluso en una
 *  sesión anterior), `requestPermission()` resuelve sin prompt. Por eso al
 *  login llamamos `tryAutoSubscribe()` sin molestar al user — solo se le
 *  pide permission si hace click explícito en el botón "Activar notificaciones".
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private http = inject(HttpClient);
  private router = inject(Router);

  /** Estado de soporte del browser. */
  readonly supported = signal<boolean>(
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );

  /** Estado de permission del browser (default/granted/denied). */
  readonly permission = signal<NotificationPermission>(
    this.supported() ? Notification.permission : 'default'
  );

  /** `true` si hay una subscription activa registrada en este browser. */
  readonly subscribed = signal<boolean>(false);

  /** Cache del registration del SW para no llamar `.register()` múltiples veces. */
  private _swRegistration: ServiceWorkerRegistration | null = null;

  constructor() {
    // Listener de mensajes del SW. Lo registramos en el constructor (no en
    // `ensureServiceWorker`) para que el handler exista ANTES de que el SW
    // mande el primer postMessage tras un click en notification — sino
    // tendríamos una race con la app booteando.
    // La app usa HashLocationStrategy; el `url` viene SIN el `#/` ("/chat?u=N").
    // Angular Router lo maneja correctamente vía `navigateByUrl`.
    if (this.supported()) {
      navigator.serviceWorker.addEventListener('message', (ev) => {
        if (ev.data?.type === 'push-click' && ev.data?.url) {
          this.router.navigateByUrl(ev.data.url);
        }
      });
    }
  }

  /**
   * Registra el service worker (idempotente) y devuelve el registration.
   * Si el browser no soporta SW o el registro falla, devuelve null.
   */
  async ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.supported()) return null;
    if (this._swRegistration) return this._swRegistration;
    try {
      this._swRegistration = await navigator.serviceWorker.register('/sw-push.js');
      return this._swRegistration;
    } catch (err) {
      console.warn('[push] no se pudo registrar el service worker:', err);
      return null;
    }
  }

  /**
   * Suscribirse explícitamente — pide permission si no fue dado todavía.
   * Llama al backend para registrar el endpoint.
   *
   * Devuelve `true` si se suscribió OK, `false` en cualquier caso de fallo
   * (permission denegado, browser sin soporte, backend down, VAPID no configurado).
   */
  async subscribe(): Promise<boolean> {
    if (!this.supported()) return false;

    const reg = await this.ensureServiceWorker();
    if (!reg) return false;

    // Pedir permission al user (no-op si ya fue otorgado antes).
    const perm = await Notification.requestPermission();
    this.permission.set(perm);
    if (perm !== 'granted') return false;

    // Obtener VAPID public key del backend.
    let publicKey: string;
    try {
      const resp = await firstValueFrom(
        this.http.get<{ publicKey: string }>('/api/push/vapid-public-key')
      );
      publicKey = resp.publicKey;
    } catch (err) {
      console.warn('[push] backend sin VAPID configurado:', err);
      return false;
    }

    // PushManager.subscribe: si ya hay sub existente, reusa.
    let sub: PushSubscription;
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      console.error('[push] PushManager.subscribe falló:', err);
      return false;
    }

    // Enviar la sub al backend (idempotente — re-subscribe actualiza el owner).
    try {
      const json = sub.toJSON();
      await firstValueFrom(
        this.http.post('/api/push/subscribe', {
          endpoint: sub.endpoint,
          p256dh: json.keys?.p256dh,
          auth: json.keys?.auth,
        })
      );
      this.subscribed.set(true);
      return true;
    } catch (err) {
      console.error('[push] /api/push/subscribe falló:', err);
      return false;
    }
  }

  /**
   * Si el browser ya tenía permission y subscription previos, los re-vincula
   * con el backend automáticamente sin pedir nada al user. Idempotente.
   * Se llama tras el login y al boot del app.
   */
  async tryAutoSubscribe(): Promise<void> {
    if (!this.supported() || Notification.permission !== 'granted') return;

    const reg = await this.ensureServiceWorker();
    if (!reg) return;

    const existing = await reg.pushManager.getSubscription();
    if (!existing) return;

    // Re-registramos en el backend por si la última sub está sin owner por
    // un logout previo o cambio de user.
    try {
      const json = existing.toJSON();
      await firstValueFrom(
        this.http.post('/api/push/subscribe', {
          endpoint: existing.endpoint,
          p256dh: json.keys?.p256dh,
          auth: json.keys?.auth,
        })
      );
      this.subscribed.set(true);
    } catch (err) {
      // No abortamos — el push solo no funcionará para este user.
      console.warn('[push] auto-subscribe falló:', err);
    }
  }

  /**
   * Desuscribirse del push service del browser Y borrar el endpoint en backend.
   * Útil para logout o para que el user pueda apagar push manualmente.
   */
  async unsubscribe(): Promise<void> {
    if (!this.supported()) return;
    const reg = await this.ensureServiceWorker();
    if (!reg) return;
    const existing = await reg.pushManager.getSubscription();
    if (!existing) {
      this.subscribed.set(false);
      return;
    }
    try {
      await firstValueFrom(
        this.http.request('DELETE', '/api/push/unsubscribe', {
          body: { endpoint: existing.endpoint },
        })
      );
    } catch (err) {
      console.warn('[push] DELETE /unsubscribe falló (sigo localmente):', err);
    }
    await existing.unsubscribe();
    this.subscribed.set(false);
  }
}

/**
 * Convierte la VAPID public key (base64url) al `Uint8Array` que pide
 * `PushManager.subscribe({applicationServerKey})`.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) output[i] = rawData.charCodeAt(i);
  return output;
}
